import { DialogModule } from '@angular/cdk/dialog';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { EditorModule } from '@tinymce/tinymce-angular';
import {
  AlertModule,
} from 'webcommon/ap-ngx-bootstrap/alert';
import {
  CollapseModule,
} from 'ngx-bootstrap/collapse';
import {
  BsDatepickerModule,
} from 'ngx-bootstrap/datepicker';
import {
  PaginationModule,
} from 'ngx-bootstrap/pagination';
import {
  TabsModule,
} from 'ngx-bootstrap/tabs';

import { MatBadgeModule } from '@angular/material/badge';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyChipsModule as MatChipsModule } from '@angular/material/legacy-chips';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySliderModule as MatSliderModule } from '@angular/material/legacy-slider';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';

import {
  ReactiveFormsModule,
} from '@angular/forms';

import { MatGridListModule } from '@angular/material/grid-list';
import {
  ApAddressComponent,
} from './ap-address';
import {
  ApAlertComponent,
} from './ap-alert';
import {
  ApComboboxComponent,
} from './ap-combobox';
import {
  ApUIConfigurationSaveHeaderComponent,
} from './ap-configuration';
import {
  ApConfirmModalComponent,
} from './ap-confirm';
import {
  ApDatePickerComponent, ApDatePickerInputDirective,
} from './ap-date-picker';
import {
  ApDatePickerNextPrevComponent,
} from './ap-datepicker-next-prev';
import { ApDropdownItemMenuComponent } from './ap-dropdown-item-menu';
import { ApDynamicComponentEditorComponent, ApDynamicComponentViewerComponent } from './ap-dynamic-component';
import {
  ApErrorMessageComponent,
} from './ap-error-message';
import {
  ApFileUploadComponent,
} from './ap-file-upload';
import {
  ApFileUploadModalComponent,
} from './ap-file-upload-modal';
import { ApIframeComponent, ApIframeDirective } from './ap-iframe';
import { ApIframeModalComponent } from './ap-iframe-modal';
import { ApInputClearComponent } from './ap-input-clear';
import {
  ApLoadingContentComponent,
} from './ap-loading-content';
import {
  ApNumericUpDownComponent,
} from './ap-numeric-up-down';
import { ApPillComponent } from './ap-pill';
import {
  ApSearchListComponent,
} from './ap-search-list';
import {
  ApSelectComponent,
} from './ap-select';
import { ApSliderComponent } from './ap-slider';
import { ApTileComponent } from './ap-tile/ap-tile.component';
import {
  ApTimeFrameComponent,
  ApTimeFrameSelectorModalComponent,
} from './ap-timeframe';
import {
  ApWarningModalComponent,
  ApWarningUnsavedChangesModalComponent,
} from './ap-warning';
import { ApCVAViewToModelChangeDirective } from './base-control';
import {
  ApAutofocusDirective,
  ApCheckboxDirective,
  ApColorBorderDirective,
  ApDecimalPlacesDirective,
  ApDisableControlDirective,
  ApDynamicSizeFromAncestorDirective,
  ApFieldInvalidDirective,
  ApFixedHeaderDirective,
  ApFormFocusDirective,
  ApHeightDirective,
  ApLongTouchDirective,
  ApMaxHeightDirective,
  ApMinMaxValueDirective,
  ApNumberValidationDirective,
  ApPhoneNumberMaskDirective,
  ApReactiveSsnFieldDirective,
  ApRequiredDirective,
  ApRequiredFromMetadataDirective,
  ApResizedDirective,
  ApSpinnerDirective,
  ApZoomableDirective,
  ContentEditableDirective,
} from './directives/index';
import {
  ApDocumentViewerComponent,
  ApDocumentViewerModalComponent,
  ApEnhancedDocumentViewerComponent,
  ApFoxitViewerDirective,
  ApImageViewerComponent,
  ApPdfComponent,
  ApSaveToFileDirective,
  ApSignPdfComponent,
} from './document-viewing';
import {
  ToCurrentlyLoadingPipe,
  ToErrorMessagesPipe,
  ToLoadedValuePipe,
  ToLoadingFailurePipe,
} from './loading-util';
import {
  ApPhoneNumberPipe,
  BypassSecurityResourceUrlPipe,
  CoerceToNullPipe,
  HtmlIdValueSanitizerPipe,
  InactiveFilterPipe, IsNilPipe, IsNotNilPipe,
  Nl2brPipe,
  SafeHtmlPipe,
} from './pipes/index';
import {
  ApCheckSizeDirective,
} from './virtual-scrolling';
import { ApActionButtonsComponent } from './ap-action-buttons';

const ajsComponents = [
  ApSignPdfComponent,
];


const documentViewingExports = [
  ApDocumentViewerComponent,
  ApEnhancedDocumentViewerComponent,
  ApImageViewerComponent,
  ApPdfComponent,
  ApSaveToFileDirective,
];

const modalComponents = [
  ApConfirmModalComponent,
  ApDocumentViewerModalComponent,
  ApIframeModalComponent,
  ApTimeFrameSelectorModalComponent,
  ApWarningModalComponent,
  ApWarningUnsavedChangesModalComponent,
];

const angularMaterialComponents = [
  MatBadgeModule,
  MatCheckboxModule,
  MatAutocompleteModule,
  MatButtonModule,
  MatCardModule,
  MatCheckboxModule,
  MatChipsModule,
  MatDividerModule,
  MatExpansionModule,
  MatFormFieldModule,
  MatGridListModule,
  MatIconModule,
  MatInputModule,
  MatMenuModule,
  MatSelectModule,
  MatSliderModule,
  MatSnackBarModule,
  MatRadioModule,
  MatTableModule,
  MatTooltipModule,
  MatTabsModule,
  DialogModule,
  ReactiveFormsModule,
];

const myExports = [
  ...ajsComponents,
  ...documentViewingExports,
  ...modalComponents,
  ApAddressComponent,
  ApAlertComponent,
  ApAutofocusDirective,
  ApCheckboxDirective,
  ApCheckSizeDirective,
  ApColorBorderDirective,
  ApComboboxComponent,
  ApConfirmModalComponent,
  ApDatePickerComponent,
  ApDatePickerInputDirective,
  ApDatePickerNextPrevComponent,
  ApDecimalPlacesDirective,
  ApDocumentViewerComponent,
  ApDropdownItemMenuComponent,
  ApDynamicComponentViewerComponent,
  ApDynamicComponentEditorComponent,
  ApDynamicSizeFromAncestorDirective,
  ApErrorMessageComponent,
  ApFieldInvalidDirective,
  ApFileUploadComponent,
  ApFileUploadModalComponent,
  ApFixedHeaderDirective,
  ApFormFocusDirective,
  ApHeightDirective,
  ApIframeComponent,
  ApIframeDirective,
  ApLoadingContentComponent,
  ApLongTouchDirective,
  ApMaxHeightDirective,
  ApMinMaxValueDirective,
  ApNumberValidationDirective,
  ApNumericUpDownComponent,
  ApPhoneNumberMaskDirective,
  ApPhoneNumberPipe,
  ApPillComponent,
  ApReactiveSsnFieldDirective,
  ApRequiredDirective,
  ApRequiredFromMetadataDirective,
  ApResizedDirective,
  ApSearchListComponent,
  ApSelectComponent,
  ApSliderComponent,
  ApSpinnerDirective,
  ApTileComponent,
  ApTimeFrameComponent,
  ApTimeFrameSelectorModalComponent,
  ApUIConfigurationSaveHeaderComponent,
  ApWarningModalComponent,
  ApZoomableDirective,
  CoerceToNullPipe,
  ContentEditableDirective,
  InactiveFilterPipe,
  Nl2brPipe,
  SafeHtmlPipe,
  ToCurrentlyLoadingPipe,
  ToErrorMessagesPipe,
  ToLoadedValuePipe,
  ToLoadingFailurePipe,
  ApDisableControlDirective,
  ApInputClearComponent,
  ApCVAViewToModelChangeDirective,
  HtmlIdValueSanitizerPipe,
  IsNilPipe,
  IsNotNilPipe,
  ApActionButtonsComponent
];

@NgModule({
  declarations: myExports,
  exports: [
    ...angularMaterialComponents,
    CommonModule,
    AlertModule,
    BsDatepickerModule,
    CollapseModule,
    DragDropModule,
    EditorModule,
    FormsModule,
    PaginationModule,
    ScrollingModule,
    TranslateModule,
    TabsModule,
    ...myExports
  ],
  imports: [
    ...angularMaterialComponents,
    CommonModule,
    AlertModule,
    BsDatepickerModule,
    CollapseModule,
    EditorModule,
    FormsModule,
    PaginationModule,
    ScrollingModule,
    TranslateModule,
    TabsModule,
    DragDropModule,
    BypassSecurityResourceUrlPipe,
    ApFoxitViewerDirective,
  ],
})
export class CoreUiModule {
}
